import { useEffect, useRef, useState } from "react";
import styles from "./CupomItem.module.scss";
import {
  Ellipsis,
  Pencil,
  Ban,
  CheckCircle,
  Power,
  Tag,
  Copy,
} from "lucide-react";
import { ICupom } from "shared/interfaces/ICupom";
import { useNavigate } from "react-router-dom";

interface CupomItemProps {
  cupom: ICupom;
  numeroUI?: string;
  modo?: "completo" | "simples";
}

export default function CupomItem({
  cupom,
  numeroUI,
  modo = "completo",
}: CupomItemProps) {
  const [menuAberto, setMenuAberto] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const isGlobal = cupom.tipo === "global";

  const copiarCupom = () => {
    if (cupom.code) {
      navigator.clipboard.writeText(cupom.code);
    }
  };

  // 🔹 Classe do status
  const getStatusClass = () => {
    switch (cupom.status) {
      case "Ativo":
        return styles.ativo;
      case "Inativo":
        return styles.inativo;
      case "Pendente":
        return styles.pendente;
      case "Bloqueado":
        return styles.bloqueado;
      default:
        return styles.default;
    }
  };

  const formatarData = (data: any) => {
    if (!data) return "-";
    if (data.toDate) {
      return data.toDate().toLocaleDateString("pt-BR");
    }
    return data;
  };

  // 🔹 Fecha menu ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuAberto(false);
      }
    };

    if (menuAberto) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () =>
      document.removeEventListener("mousedown", handleClickOutside);
  }, [menuAberto]);

  return (
    <div
      className={`${styles.card} ${
        isGlobal ? styles.globalCard : ""
      } ${modo === "simples" ? styles.simples : ""}`}
    >
      {/* 🔹 MODO SIMPLES (Cupons de Desconto) */}
      {modo === "simples" && (
        <div className={styles.simpleRow}>
          <div className={styles.left}>
            <Tag size={26} className={styles.icon} />

            <div className={styles.info}>
              <span className={styles.codigo}>
                {cupom.code}
              </span>

              <span className={styles.desconto}>
                {cupom.discountValue ?? "-"} de desconto
              </span>
            </div>
          </div>

          <button
            className={styles.btnCopiar}
            onClick={copiarCupom}
          >
            <Copy size={16} />
            Copiar
          </button>
        </div>
      )}

      {/* 🔹 MODO COMPLETO (Meus Cupons) */}
      {modo === "completo" && (
        <>
          {/* TOPO */}
          <div className={styles.topRow}>
            <span className={styles.codigo}>
              {cupom.code ?? `#${numeroUI}`}
            </span>

            {!isGlobal && (
              <div className={styles.menuWrapper} ref={menuRef}>
                <button
                  className={styles.menuBtn}
                  onClick={() =>
                    setMenuAberto((prev) => !prev)
                  }
                >
                  <Ellipsis size={18} />
                </button>

                {menuAberto && (
                  <div className={styles.menuPopup}>
                    <button
                      onClick={() => {
                        navigate(`/cupons/editar/${cupom.uid}`);
                        setMenuAberto(false);
                      }}
                    >
                      <Pencil size={14} />
                      Editar
                    </button>

                    {cupom.status === "Inativo" ? (
                      <button
                        className={styles.success}
                        onClick={() => {
                          setMenuAberto(false);
                        }}
                      >
                        <CheckCircle size={14} />
                        Ativar
                      </button>
                    ) : (
                      <button
                        className={styles.danger}
                        onClick={() => {
                          setMenuAberto(false);
                        }}
                      >
                        <Ban size={14} />
                        Inativar
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* NOME + DESCONTO */}
          <div className={styles.mainRow}>
            <h3 className={styles.nome}>
              {cupom.nome ?? "Cupom sem nome"}
            </h3>

            <span className={styles.desconto}>
              {cupom.discountValue?? "-"}
            </span>
          </div>

          {/* STATUS */}
          <div className={styles.statusRow}>
            <span
              className={`${styles.status} ${getStatusClass()}`}
            >
              {cupom.status}
            </span>
          </div>

          {/* RODAPÉ */}
          <div className={styles.footerRow}>
            <span>
              {formatarData(cupom.comecaEm)} -{" "}
              {formatarData(cupom.expiresAt)}
            </span>

            <span className={styles.utilizados}>
              <Power size={14} />
              Utilizados: {cupom.maxUses ?? 0}
            </span>
          </div>
        </>
      )}
    </div>
  );
}